import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, MessageSquareHeart, LifeBuoy } from "lucide-react";
import { API } from "@/lib/gp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function Support() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, subject: form.subject || "Support request" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not send");
      setSent(true);
      toast.success(data.message || "Message sent!");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (e) {
      toast.error("Something went wrong — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-5 md:px-8 py-16 md:py-20 grid lg:grid-cols-2 gap-12">
      <div>
        <span className="font-vt323 uppercase tracking-widest text-lg text-gp-blue">Support</span>
        <h1 className="font-unbounded font-black tracking-tighter text-4xl sm:text-5xl mt-2">We've got your back</h1>
        <p className="text-white/65 mt-4 text-lg leading-relaxed">
          Question, bug, billing hiccup, or a game you'd love Yorick to cover next? Send it over
          and a human (plus one very helpful skull) will get back to you.
        </p>

        <div className="mt-8 space-y-4">
          <div className="glass rounded-2xl p-5 flex items-start gap-4">
            <span className="grid place-items-center h-11 w-11 rounded-xl bg-gp-blue/15 text-gp-blue"><Mail size={20} /></span>
            <div>
              <p className="font-semibold">Email us</p>
              <a href="mailto:info@gameplatplay.com" className="text-white/60 text-sm hover:text-gp-gold" data-testid="support-email">
                info@gameplatplay.com
              </a>
            </div>
          </div>
          <div className="glass rounded-2xl p-5 flex items-start gap-4">
            <span className="grid place-items-center h-11 w-11 rounded-xl bg-gp-gold/15 text-gp-gold"><MessageSquareHeart size={20} /></span>
            <div>
              <p className="font-semibold">Ask Yorick first</p>
              <p className="text-white/60 text-sm">Most strategy questions are answered instantly in chat.</p>
            </div>
          </div>
          <div className="glass rounded-2xl p-5 flex items-start gap-4">
            <span className="grid place-items-center h-11 w-11 rounded-xl bg-gp-green/15 text-gp-green"><LifeBuoy size={20} /></span>
            <div>
              <p className="font-semibold">Typical reply time</p>
              <p className="text-white/60 text-sm">Within 24 hours, usually much faster.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="glass rounded-3xl p-7 md:p-8">
        {sent ? (
          <div className="h-full flex flex-col items-center justify-center text-center py-16" data-testid="support-success">
            <span className="grid place-items-center h-16 w-16 rounded-2xl bg-gp-green/15 text-gp-green mb-5"><MessageSquareHeart size={30} /></span>
            <h3 className="font-unbounded font-bold text-2xl">Message received</h3>
            <p className="text-white/60 mt-2 max-w-sm">Yorick tucked your note safely in the deck. We'll be in touch soon.</p>
            <Button onClick={() => setSent(false)} className="mt-6 rounded-full bg-gp-blue">Send another</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="support-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-white/70">Name</label>
                <Input data-testid="support-name" value={form.name} onChange={set("name")} placeholder="Your name" className="mt-1.5 bg-black/30 border-white/10" />
              </div>
              <div>
                <label className="text-sm text-white/70">Email</label>
                <Input data-testid="support-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="mt-1.5 bg-black/30 border-white/10" />
              </div>
            </div>
            <div>
              <label className="text-sm text-white/70">Subject</label>
              <Input data-testid="support-subject" value={form.subject} onChange={set("subject")} placeholder="What's this about?" className="mt-1.5 bg-black/30 border-white/10" />
            </div>
            <div>
              <label className="text-sm text-white/70">Message</label>
              <Textarea data-testid="support-message" value={form.message} onChange={set("message")} placeholder="Tell us everything…" rows={6} className="mt-1.5 bg-black/30 border-white/10 resize-none" />
            </div>
            <Button
              type="submit"
              data-testid="support-submit"
              disabled={busy}
              className="w-full rounded-full py-6 bg-gp-gold hover:bg-gp-gold/90 text-gp-bg font-bold shadow-[0_0_22px_rgba(240,192,64,0.35)]"
            >
              {busy ? <Loader2 className="animate-spin" size={18} /> : "Send message"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
